@file:Suppress("ktlint:standard:no-wildcard-imports")

package xyz.malefic.multipage.components

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaBars
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import xyz.malefic.multipage.i18n.Language
import xyz.malefic.multipage.i18n.LocalLanguage
import xyz.malefic.multipage.i18n.t
import xyz.malefic.multipage.styles.Theme

@Composable
fun Header() {
    val pageContext = rememberPageContext()
    var mobileMenuOpen by remember { mutableStateOf(false) }

    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .backgroundColor(rgba(255, 255, 255, 0.97f))
                .borderBottom(width = 1.px, style = LineStyle.Solid, color = rgba(72, 90, 120, 0.12f))
                .position(Position.Sticky)
                .top(0.px)
                .zIndex(Theme.ZIndexSticky),
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1200.px)
                    .margin(leftRight = 0.px) // auto margin handled by parent Box centering
                    .padding(leftRight = 24.px, topBottom = 16.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            // Logo
            Row(
                modifier =
                    Modifier
                        .gap(10.px)
                        .cursor(Cursor.Pointer)
                        .onClick { pageContext.router.navigateTo("/") },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Image(
                    src = "/logo.png",
                    modifier = Modifier.size(36.px),
                    alt = "LivStay Japan",
                )
                SpanText(
                    text = "LIVSTAY JAPAN",
                    modifier =
                        Modifier
                            .fontFamily(Theme.FontFamily)
                            .fontSize(18.px)
                            .fontWeight(FontWeight.Bold)
                            .color(Theme.Primary)
                            .letterSpacing(3.px),
                )
            }

            // Desktop Navigation
            if (js("window.innerWidth >= 768")) {
                Row(
                    modifier = Modifier.gap(32.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    NavLink(t("nav.home"), "/", pageContext.route.path)
                    NavLink(t("nav.services"), "/services", pageContext.route.path)
                    NavLink(t("nav.about"), "/about", pageContext.route.path)

                    LanguageToggle()

                    // CTA Button
                    Box(
                        modifier =
                            Modifier
                                .padding(leftRight = 20.px, topBottom = 10.px)
                                .backgroundColor(Theme.Primary)
                                .color(Theme.Surface)
                                .borderRadius(Theme.RadiusMd.px)
                                .cursor(Cursor.Pointer)
                                .onClick {
                                    js("window.location.href = 'mailto:info@livstayjapan.co.jp'")
                                },
                    ) {
                        SpanText(
                            text = t("nav.consultation"),
                            modifier =
                                Modifier
                                    .fontWeight(FontWeight.SemiBold)
                                    .fontSize(14.px),
                        )
                    }
                }
            }

            // Mobile Menu Toggle
            if (js("window.innerWidth < 768")) {
                Row(
                    modifier = Modifier.gap(12.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    LanguageToggle()

                    Box(
                        modifier =
                            Modifier
                                .size(44.px)
                                .backgroundColor(rgba(72, 90, 120, 0.08f))
                                .borderRadius(Theme.RadiusMd.px)
                                .cursor(Cursor.Pointer)
                                .onClick { mobileMenuOpen = !mobileMenuOpen },
                        contentAlignment = Alignment.Center,
                    ) {
                        Box(
                            modifier =
                                Modifier
                                    .color(Theme.Primary)
                                    .fontSize(18.px),
                        ) {
                            FaBars()
                        }
                    }
                }
            }
        }

        // Mobile Menu
        if (mobileMenuOpen && js("window.innerWidth < 768")) {
            Box(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(rgba(255, 255, 255, 0.98f))
                        .borderBottom(width = 1.px, style = LineStyle.Solid, color = rgba(72, 90, 120, 0.12f)),
            ) {
                Box(
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .padding(16.px)
                            .gap(12.px),
                ) {
                    val navItems =
                        listOf(
                            "nav.home" to "/",
                            "nav.services" to "/services",
                            "nav.about" to "/about",
                        )

                    navItems.forEach { (key, path) ->
                        MobileNavLink(
                            text = t(key),
                            path = path,
                            currentPath = pageContext.route.path,
                        ) { mobileMenuOpen = false }
                    }

                    // Mobile CTA
                    Box(
                        modifier =
                            Modifier
                                .margin(top = 8.px)
                                .fillMaxWidth()
                                .padding(leftRight = 16.px, topBottom = 14.px)
                                .backgroundColor(Theme.Primary)
                                .color(Theme.Surface)
                                .borderRadius(Theme.RadiusMd.px)
                                .cursor(Cursor.Pointer)
                                .onClick {
                                    js("window.location.href = 'mailto:info@livstayjapan.co.jp'")
                                },
                        contentAlignment = Alignment.Center,
                    ) {
                        SpanText(
                            text = t("nav.consultation"),
                            modifier =
                                Modifier
                                    .fontWeight(FontWeight.SemiBold)
                                    .fontSize(16.px),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun LanguageToggle() {
    val langState = LocalLanguage.current

    Row(
        modifier = Modifier.gap(4.px),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            "JP",
            modifier =
                Modifier
                    .fontSize(13.px)
                    .cursor(Cursor.Pointer)
                    .padding(leftRight = 6.px, topBottom = 4.px)
                    .borderRadius(Theme.RadiusSm.px)
                    .fontWeight(if (langState.value == Language.JA) FontWeight.Bold else FontWeight.Normal)
                    .color(if (langState.value == Language.JA) Theme.Surface else Theme.TextTertiary)
                    .backgroundColor(
                        if (langState.value == Language.JA) Theme.Primary else rgba(0, 0, 0, 0.0f),
                    ).onClick { langState.value = Language.JA },
        )
        SpanText(
            "EN",
            modifier =
                Modifier
                    .fontSize(13.px)
                    .cursor(Cursor.Pointer)
                    .padding(leftRight = 6.px, topBottom = 4.px)
                    .borderRadius(Theme.RadiusSm.px)
                    .fontWeight(if (langState.value == Language.EN) FontWeight.Bold else FontWeight.Normal)
                    .color(if (langState.value == Language.EN) Theme.Surface else Theme.TextTertiary)
                    .backgroundColor(
                        if (langState.value == Language.EN) Theme.Primary else rgba(0, 0, 0, 0.0f),
                    ).onClick { langState.value = Language.EN },
        )
    }
}

@Composable
private fun NavLink(
    text: String,
    path: String,
    currentPath: String,
) {
    val isActive = currentPath == path
    val pageContext = rememberPageContext()

    Box(
        modifier =
            Modifier
                .position(Position.Relative)
                .padding(leftRight = 4.px, topBottom = 8.px),
    ) {
        SpanText(
            text = text,
            modifier =
                Modifier
                    .color(if (isActive) Theme.Primary else Theme.TextPrimary)
                    .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
                    .fontSize(15.px)
                    .cursor(Cursor.Pointer)
                    .textDecorationLine(TextDecorationLine.None)
                    .padding(leftRight = 8.px, topBottom = 6.px)
                    .borderRadius(Theme.RadiusSm.px)
                    .backgroundColor(if (isActive) rgba(72, 90, 120, 0.08f) else rgba(0, 0, 0, 0.0f))
                    .onClick { pageContext.router.navigateTo(path) },
        )

        if (isActive) {
            Box(
                modifier =
                    Modifier
                        .position(Position.Absolute)
                        .bottom(0.px)
                        .left(20.percent)
                        .width(60.percent)
                        .height(2.px)
                        .backgroundColor(Theme.Primary)
                        .borderRadius(1.px),
            )
        }
    }
}

@Composable
private fun MobileNavLink(
    text: String,
    path: String,
    currentPath: String,
    onClick: () -> Unit,
) {
    val isActive = currentPath == path
    val pageContext = rememberPageContext()

    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .margin(bottom = 4.px)
                .padding(leftRight = 16.px, topBottom = 12.px)
                .backgroundColor(
                    if (isActive) rgba(72, 90, 120, 0.08f) else rgba(255, 255, 255, 0.5f),
                ).borderRadius(Theme.RadiusMd.px)
                .border(1.px, LineStyle.Solid, if (isActive) Theme.Primary else rgba(72, 90, 120, 0.1f))
                .cursor(Cursor.Pointer)
                .onClick {
                    onClick()
                    pageContext.router.navigateTo(path)
                },
        contentAlignment = Alignment.CenterStart,
    ) {
        SpanText(
            text = text,
            modifier =
                Modifier
                    .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
                    .fontSize(15.px)
                    .color(if (isActive) Theme.Primary else Theme.TextPrimary),
        )
    }
}
